import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../../../services/apiService";

type HighlightsPayload = { ids?: string[]; max?: number };

/**
 * The shows this person highlighted (GET/PUT/DELETE dashboard/highlights). One
 * instance in the Dashboard, shared by the overview, the table and the calendar.
 * Toggles are optimistic; the API enforces the limit (409) and the answer wins.
 * `version` bumps after every saved change so the overview reloads its board.
 */
export function useHighlights(enabled: boolean) {
  const [ids, setIds] = useState<string[]>([]);
  const [max, setMax] = useState(5);
  const [message, setMessage] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const idsRef = useRef<string[]>([]);
  idsRef.current = ids;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const { response } = await apiRequest({ method: "GET", url: "dashboard/highlights", data: null, isToken: true });
      if (cancelled || response?.status !== 200) return;
      const body = (response?.data?.data ?? {}) as HighlightsPayload;
      setIds(Array.isArray(body.ids) ? body.ids : []);
      if (typeof body.max === "number") setMax(body.max);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const toggle = useCallback(
    async (showId: string) => {
      const before = idsRef.current;
      const on = before.includes(showId);
      if (!on && before.length >= max) {
        setMessage(`You can highlight up to ${max} shows. Remove one to add another.`);
        return;
      }
      setIds(on ? before.filter((id) => id !== showId) : [...before, showId]);
      const { response, error } = await apiRequest({
        method: on ? "DELETE" : "PUT",
        url: `dashboard/highlights/${showId}`,
        data: null,
        isToken: true,
      });
      if (error || response?.status !== 200) {
        setIds(before);
        setMessage(error?.response?.data?.message || "Could not save the highlight. Try again.");
        return;
      }
      const body = (response?.data?.data ?? {}) as HighlightsPayload;
      if (Array.isArray(body.ids)) setIds(body.ids);
      setVersion((v) => v + 1);
    },
    [max],
  );

  const idSet = useMemo(() => new Set(ids), [ids]);
  const clearMessage = useCallback(() => setMessage(null), []);

  return { ids: idSet, max, toggle, message, clearMessage, version };
}
